/**
 * VH-274: put a recipe on the shopping list from the shopping list.
 *
 * Reto, 2026-10-07: "I cannot create a shopping list out of the recipe." The button
 * existed (VH-260), a screen and a half down the recipe page — and the list itself,
 * which is where one goes to make a list, named three ways to fill it and none of
 * them was a recipe. Closed until asked for, so the list stays the screen.
 */

import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { BookOpenIcon, ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline'

import { useRecipes } from '../../hooks/useRecipes'

export default function RecipePicker({ onPick, pendingId = null }) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  // Asked for only once the picker is opened: most visits to the list never need it.
  const { data, isLoading, error } = useRecipes({}, { enabled: open })
  const recipes = data?.items || []

  return (
    <div className="rounded-lg border border-outline-variant" data-testid="shopping-recipe-picker">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        className="flex w-full items-center justify-center gap-2 px-3 min-h-[44px] font-medium text-on-surface"
        data-testid="shopping-from-recipe"
      >
        <BookOpenIcon className="h-5 w-5" aria-hidden="true" />
        {t('shopping.recipe.button')}
        {open ? (
          <ChevronUpIcon className="h-4 w-4" aria-hidden="true" />
        ) : (
          <ChevronDownIcon className="h-4 w-4" aria-hidden="true" />
        )}
      </button>

      {open && (
        <div className="border-t border-outline-variant">
          {isLoading && <p className="p-3 text-sm text-on-surface-variant">{t('common.loading')}</p>}
          {error && (
            <p role="alert" className="p-3 text-sm text-danger-600">
              {t('shopping.recipe.loadFailed')}
            </p>
          )}
          {!isLoading && !error && recipes.length === 0 && (
            <p className="p-3 text-sm text-on-surface-variant" data-testid="shopping-recipe-none">
              {t('shopping.recipe.none')}
            </p>
          )}
          {recipes.length > 0 && (
            <ul className="max-h-72 overflow-y-auto">
              {recipes.map((recipe) => (
                <li key={recipe.id} className="border-b border-outline-variant last:border-b-0">
                  <button
                    type="button"
                    onClick={() => onPick(recipe)}
                    disabled={pendingId !== null}
                    className="flex w-full items-center justify-between gap-3 px-3 min-h-[44px] text-left text-sm text-on-surface disabled:opacity-50"
                    data-testid={`shopping-recipe-${recipe.id}`}
                  >
                    <span>{recipe.title}</span>
                    <span className="shrink-0 text-xs text-on-surface-variant">
                      {pendingId === recipe.id ? t('shopping.recipe.adding') : t('shopping.recipe.add')}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  )
}
